import { isRecord } from '@/lib/shared/guards/is-record'

/**
 * A catalog: nested objects whose leaves are messages. With
 * `experimental.messages.precompile` a leaf may be the array AST the build
 * compiled the ICU string into.
 *
 * Client-safe, though the merges run on the server: its request config and
 * the island catalogs `LazyMessages` fetches
 * (`app/i18n/islands/[locale]/[namespace]/route.ts`).
 */
export type MessageTree = Record<string, unknown>

/**
 * `target` with every message it lacks taken from `source`, so a gap in a
 * translation renders English instead of a raw key. Keys `source` no longer
 * has are dropped.
 */
export function withFallback(
  source: MessageTree,
  target: MessageTree | undefined
): MessageTree {
  const merged: MessageTree = {}
  for (const [key, sourceValue] of Object.entries(source)) {
    const targetValue = target?.[key]
    if (isRecord(sourceValue)) {
      merged[key] = withFallback(
        sourceValue,
        isRecord(targetValue) ? targetValue : undefined
      )
    } else {
      merged[key] =
        targetValue !== undefined && !isRecord(targetValue)
          ? targetValue
          : sourceValue
    }
  }
  return merged
}

/**
 * `base` with `top` laid over it, message by message: what `top` has wins,
 * and everything else `base` has stays. A nested provider uses it, so one
 * that gives part of a namespace (`'settings.mcp'`) keeps the rest an
 * outer one gave, and the other way round.
 */
export function overlayCatalog(
  base: MessageTree,
  top: MessageTree
): MessageTree {
  const merged: MessageTree = { ...base }
  for (const [key, value] of Object.entries(top)) {
    const under = base[key]
    merged[key] =
      isRecord(under) && isRecord(value) ? overlayCatalog(under, value) : value
  }
  return merged
}

/**
 * Whether two catalogs hold the same messages, compared by content (a
 * compiled message is an array of strings, numbers and objects). A refresh
 * sends a page's catalogs again as new objects with the same content.
 */
export function isSameCatalog(a: unknown, b: unknown): boolean {
  if (a === b) {
    return true
  }
  if (Array.isArray(a) || Array.isArray(b)) {
    return (
      Array.isArray(a) &&
      Array.isArray(b) &&
      a.length === b.length &&
      a.every((value, index) => isSameCatalog(value, b[index]))
    )
  }
  if (!(isRecord(a) && isRecord(b))) {
    return false
  }
  const keys = Object.keys(a)
  return (
    keys.length === Object.keys(b).length &&
    keys.every((key) => Object.hasOwn(b, key) && isSameCatalog(a[key], b[key]))
  )
}
